import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useDataSync, useRouteRefresh } from '../context/DataSyncContext';
import '../styles/my-entries.css';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import type { ArtifactKind, Flow, FlowStatus } from '../types';

type StatusFilter = FlowStatus | 'all';

export function MyEntriesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [entries, setEntries] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<ArtifactKind>('flow');
  const { refreshAllBadges } = useDataSync();
  const refreshToken = useRouteRefresh('/my');
  const autoRefreshTick = useAutoRefresh(5000, { enabled: Boolean(user) });
  const requestIdRef = useRef(0);
  const pendingScrollRef = useRef<number | null>(null);
  const scrollPersistedRef = useRef(false);
  const entriesLinkState = useMemo(
    () => ({
      from: {
        pathname: location.pathname,
        search: location.search,
        label: 'My entries'
      }
    }),
    [location.pathname, location.search]
  );

  useEffect(() => {
    const stored = sessionStorage.getItem('my-entries:scroll');
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed)) {
        pendingScrollRef.current = parsed;
      }
      sessionStorage.removeItem('my-entries:scroll');
    }
    scrollPersistedRef.current = false;
  }, []);

  const persistScrollPosition = useCallback(() => {
    try {
      sessionStorage.setItem('my-entries:scroll', String(window.scrollY));
    } catch (err) {
      console.warn('Unable to store my entries scroll position', err);
    }
    scrollPersistedRef.current = true;
  }, []);

  const restorePendingScroll = useCallback(() => {
    const target = pendingScrollRef.current;
    if (target === null) {
      return;
    }
    const scrollNow = () => window.scrollTo({ top: target });
    requestAnimationFrame(() => {
      scrollNow();
      requestAnimationFrame(scrollNow);
    });
    pendingScrollRef.current = null;
    scrollPersistedRef.current = false;
    sessionStorage.removeItem('my-entries:scroll');
  }, []);

  useEffect(
    () => () => {
      if (!scrollPersistedRef.current) {
        persistScrollPosition();
      }
    },
    [persistScrollPosition]
  );

  const fetchEntries = useCallback(
    async ({ silent = false } = {}) => {
      if (!user) {
        setEntries([]);
        setLoading(false);
        setError(null);
        return;
      }

      const requestId = ++requestIdRef.current;

      if (!silent) {
        setLoading(true);
        setError(null);
      }

      try {
        const payload = await api.flows({
          ownerId: user.id,
          kind: kindFilter,
          status: statusFilter === 'all' ? undefined : statusFilter
        });

        if (requestIdRef.current !== requestId) {
          return;
        }

        setEntries(
          payload.map((entry) => ({
            ...entry,
            kind: entry.kind === 'node' ? 'node' : 'flow'
          }))
        );
        void refreshAllBadges();
        restorePendingScroll();
      } catch (err) {
        if (requestIdRef.current !== requestId) {
          return;
        }

        if (silent) {
          console.error('My entries background refresh failed', err);
        } else {
          setError((err as Error).message);
        }
      } finally {
        if (requestIdRef.current !== requestId) {
          return;
        }

        if (!silent) {
          setLoading(false);
        }
      }
    },
    [kindFilter, refreshAllBadges, restorePendingScroll, statusFilter, user]
  );

  useEffect(() => {
    if (!user) {
      setEntries([]);
      setLoading(false);
      setError(null);
      return;
    }

    const timer = window.setTimeout(() => {
      void fetchEntries();
    }, 150);

    return () => {
      window.clearTimeout(timer);
    };
  }, [fetchEntries, refreshToken, user]);

  useEffect(() => {
    if (!autoRefreshTick || !user) {
      return;
    }

    void fetchEntries({ silent: true });
  }, [autoRefreshTick, fetchEntries, user]);

  const groupedByStatus = useMemo(() => {
    return entries.reduce<Record<FlowStatus, Flow[]>>(
      (acc, entry) => {
        acc[entry.status].push(entry);
        return acc;
      },
      { approved: [], pending: [], rejected: [] }
    );
  }, [entries]);

  if (!user) {
    return <div className="alert alert-error">You need to be signed in to view your entries.</div>;
  }

  return (
    <div className="my-entries">
      <div className="my-entries__header">
        <div>
          <h1>My entries</h1>
          <p>Monitor and update everything you have contributed to the marketplace.</p>
        </div>
        <Link className="button" to="/flows/new">
          Create new entry
        </Link>
      </div>

      <div className="my-entries__filters">
        <div>
          <span className="my-entries__filter-label">Status</span>
          <div className="tag-group">
            {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
              <button
                key={status}
                type="button"
                className={`chip ${statusFilter === status ? 'chip-selected' : ''}`}
                onClick={() => setStatusFilter(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="my-entries__filter-label">Kind</span>
          <div className="tag-group">
            {(['flow', 'node'] as const).map((kindOption) => (
              <button
                key={kindOption}
                type="button"
                className={`chip ${kindFilter === kindOption ? 'chip-selected' : ''}`}
                onClick={() => setKindFilter(kindOption)}
              >
                {kindOption === 'flow' ? 'Flows' : 'Nodes'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading ? (
        <div className="loading">Loading your entries…</div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <h3>No entries yet</h3>
          <p>Create your first flow or node to see it listed here.</p>
        </div>
      ) : (
        <div className="my-entries__sections">
          {(['pending', 'rejected', 'approved'] as FlowStatus[]).map((status) => {
            const items =
              statusFilter === 'all'
                ? groupedByStatus[status]
                : status === statusFilter
                ? groupedByStatus[status]
                : [];

            if (items.length === 0) {
              return null;
            }

            return (
              <section key={status} className="my-entries__section">
                <header>
                  <h2>
                    {status === 'pending'
                      ? 'Awaiting review'
                      : status === 'rejected'
                      ? 'Needs attention'
                      : 'Approved'}
                  </h2>
                  <span className={`status status-${status}`}>{items.length}</span>
                </header>
                <ul className="my-entries__list">
                  {items.map((entry) => (
                    <li key={entry.id} className="my-entries__item">
                      <div>
                        <Link
                          to={`/flows/${entry.id}`}
                          state={entriesLinkState}
                          onClickCapture={persistScrollPosition}
                        >
                          {entry.title}
                        </Link>
                        <p className="my-entries__item-meta">
                          Updated {new Date(entry.updatedAt).toLocaleString()} •{' '}
                          {entry.kind === 'node' ? 'Node' : 'Flow'}
                          {entry.flowType ? ` • ${entry.flowType}` : ''}
                          {entry.flowTypeDetail ? ` — ${entry.flowTypeDetail}` : ''}
                        </p>
                      </div>
                      <div className="my-entries__item-actions">
                        <Link
                          className="button button-primary"
                          to={`/flows/${entry.id}`}
                          state={entriesLinkState}
                          onClickCapture={persistScrollPosition}
                        >
                          View
                        </Link>
                        <Link className="button button-secondary" to={`/flows/${entry.id}/edit`}>
                          Edit
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
